import { h } from 'preact'
import { useState } from 'preact/hooks'
import { i18n } from 'i18n/i18n'
import { formatNumber, formatNumberTrimZeros } from 'utils/format-number'
import type { Calculation, ExchangeDirection } from 'models/exchange-transfer-calculate'

type CtaVariant = 'link' | 'button'

interface CalculatorFooterProps {
  exchangeData: Calculation
  direction: ExchangeDirection
  counterCurrency: string
  ctaHref?: string
  discountRate?: number
  ctaVariant?: CtaVariant
  lang?: string
}

export default function CalculatorFooter({
  exchangeData,
  direction,
  counterCurrency,
  ctaHref,
  ctaVariant = 'link',
  discountRate,
}: CalculatorFooterProps) {
  const [showOptions, setShowOptions] = useState(false)

  const exchangeAmount = Number(exchangeData.exchange_amount) || 0
  const feeAmount = Number(exchangeData.fee_amount) || 0
  const feePercent = Number(exchangeData.fee_percent) || 0
  const isPromotionActive = discountRate !== 0

  const commissionLabel = String(
    i18n(
      isPromotionActive ? 'widgetCalculator.commission' : 'calculator.commissionWT',
      isPromotionActive ? { commission: formatNumberTrimZeros(feePercent) } : undefined,
    ),
  )
  const amountLabel = String(
    i18n(
      isPromotionActive
        ? `widgetCalculator.exchangeAmount${direction === 'forward' ? 'BuyLabel' : 'SellLabel'}`
        : 'calculator.amountText',
    ),
  )

  const ctaText = String(i18n(ctaVariant === 'button' ? 'calculator.continueButton' : 'widgetCalculator.buttonText'))
  const isButton = ctaVariant === 'button'

  return (
    <div className="calc-widget__footer">
      <div className="calc-widget__summary">
        <p className="calc-widget__actual-rate-fee">
          <span className="calc-widget__actual-rate-fee-text">{commissionLabel}:</span>
          <b className="calc-widget__actual-rate-fee-value">
            &nbsp;{formatNumber(feeAmount, 2)}&nbsp;{counterCurrency}
          </b>
        </p>
        <p className="calc-widget__exchange-amount">
          <span className="calc-widget__exchange-amount-text">{amountLabel}:</span>
          <b className="calc-widget__exchange-amount-value">
            &nbsp;{formatNumber(exchangeAmount, 2)}&nbsp;{counterCurrency}
          </b>
        </p>
      </div>

      {!isButton && (
        <a
          className="calc-widget__cta button is-bold"
          href={ctaHref || '#'}
          {...(ctaHref ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {ctaText}
        </a>
      )}

      {isButton && (
        <div className="continuing-options__wrap-inner">
          <div className="continuing-options__wrapper">
            <button
              type="button"
              className="calc-widget__cta button is-bold"
              data-track-click="anchor-button"
              aria-expanded={showOptions}
              aria-controls="continuing-options"
              onClick={() => setShowOptions((prev) => !prev)}
            >
              {ctaText}
            </button>

            <div
              id="continuing-options"
              className="continuing-options"
              data-buttons-wrap=""
              hidden={showOptions ? undefined : true}
              aria-hidden={!showOptions}
            >
              <a
                className="continuing-options__option"
                href={`https://panel.walutomat.pl/logowanie?lang=${this.props.lang || 'pl'}`}
                data-track-click="anchor-button"
                onClick={() => setShowOptions(false)}
              >
                {i18n('continueButton.logIn')}
              </a>
              <a
                className="continuing-options__option"
                href={`https://user.walutomat.pl/?lang=${this.props.lang || 'pl'}#/registration`}
                data-track-click="anchor-button"
                onClick={() => setShowOptions(false)}
              >
                {i18n('continueButton.createAccount')}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
